/**
 * PotionEffectDisplay - HUD overlay that shows active potion effects
 * in the top-right corner, Minecraft style.
 *
 * Each active effect shows a colored icon, the effect name, and the
 * time remaining.  Effects fade out when they expire.
 */

export class PotionEffectDisplay {
  /**
   * @param {import('../items/PotionSystem.js').PotionSystem} potionSystem
   */
  constructor(potionSystem) {
    this.potionSystem = potionSystem;

    // Container
    this.container = document.createElement('div');
    this.container.id = 'potion-effects-hud';
    this.container.style.cssText = `
      position: fixed;
      top: 8px;
      right: 8px;
      display: flex;
      flex-direction: column;
      gap: 4px;
      z-index: 90;
      pointer-events: none;
      font-family: 'Courier New', monospace;
      image-rendering: pixelated;
    `;
    document.body.appendChild(this.container);

    /** @type {Map<string, HTMLElement>} */
    this._elements = new Map();
  }

  /**
   * Refresh the display from the potion system.
   * Call once per frame (or once per second -- low frequency is fine).
   */
  update() {
    const effects = this.potionSystem.getActiveEffects();
    const activeIds = new Set(effects.map(e => e.id));

    // Remove elements for effects that are no longer active
    for (const [id, el] of this._elements) {
      if (!activeIds.has(id)) {
        // Fade out then remove
        el.style.transition = 'opacity 0.4s';
        el.style.opacity = '0';
        setTimeout(() => {
          if (el.parentNode) el.parentNode.removeChild(el);
        }, 400);
        this._elements.delete(id);
      }
    }

    // Add / update elements for active effects
    for (const effect of effects) {
      let el = this._elements.get(effect.id);
      if (!el) {
        el = this._createEffectElement(effect);
        this.container.appendChild(el);
        this._elements.set(effect.id, el);
      }
      // Update time text
      this._updateEffectElement(el, effect);
    }
  }

  /**
   * @param {{ id: string, name: string, timeLeft: number, color: string }} effect
   * @returns {HTMLElement}
   */
  _createEffectElement(effect) {
    const row = document.createElement('div');
    row.style.cssText = `
      display: flex;
      align-items: center;
      gap: 6px;
      background: rgba(0, 0, 0, 0.55);
      padding: 4px 8px;
      border-radius: 2px;
      border: 1px solid rgba(255,255,255,0.1);
      opacity: 1;
    `;

    // Colored icon (small square)
    const icon = document.createElement('div');
    icon.style.cssText = `
      width: 14px;
      height: 14px;
      border-radius: 2px;
      background: ${effect.color};
      border: 1px solid rgba(255,255,255,0.25);
      box-shadow: inset 1px 1px 0 rgba(255,255,255,0.3),
                  inset -1px -1px 0 rgba(0,0,0,0.2);
      flex-shrink: 0;
    `;
    row.appendChild(icon);

    // Name
    const name = document.createElement('span');
    name.className = 'effect-name';
    name.textContent = effect.name;
    name.style.cssText = `
      color: #E0E0E0;
      font-size: 11px;
      text-shadow: 1px 1px 0 #000;
      white-space: nowrap;
    `;
    row.appendChild(name);

    // Time
    const time = document.createElement('span');
    time.className = 'effect-time';
    time.style.cssText = `
      color: #AAAAAA;
      font-size: 10px;
      text-shadow: 1px 1px 0 #000;
      margin-left: 4px;
      white-space: nowrap;
    `;
    row.appendChild(time);

    return row;
  }

  /**
   * @param {HTMLElement} el
   * @param {{ timeLeft: number }} effect
   */
  _updateEffectElement(el, effect) {
    const timeSpan = el.querySelector('.effect-time');
    if (timeSpan) {
      const mins = Math.floor(effect.timeLeft / 60);
      const secs = Math.floor(effect.timeLeft % 60);
      timeSpan.textContent = mins > 0
        ? `${mins}:${secs.toString().padStart(2, '0')}`
        : `${secs}s`;

      // Flash when low time
      if (effect.timeLeft <= 10) {
        timeSpan.style.color = effect.timeLeft % 1 < 0.5 ? '#FF6666' : '#AAAAAA';
      } else {
        timeSpan.style.color = '#AAAAAA';
      }
    }
  }
}
